import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";
import { INSIGHT_RULE_CODES } from "../catalog/insight-rules.ts";

/**
 * Business insights (`12-chain.md` § 5).
 *
 * Four surfaces: headline figures against the period before, a series across
 * stores, how one item's price varies between them, and the anomalies. Every
 * one of them is a question about the chain that no single store can answer,
 * which is the only reason they live above the store surface.
 */

const Money = z.string().regex(/^-?\d+$/, "minor units, as digits");
const Period = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "a business date");

export const InsightPeriodQuerySchema = z.object({
  from: Period,
  to: Period,
  /** One store out of the chain. Absent means all of them. */
  storeId: z.uuid().optional(),
});

/**
 * A figure, what it was last time, and by how much it moved.
 *
 * The comparison period is the same length shifted back a whole number of
 * weeks — never simply "the previous N days", which would compare a week
 * containing two Saturdays against one containing none.
 */
export const InsightFigureSchema = z.object({
  key: z.string(),
  value: Money,
  previous: Money,
  change: Money,
  /** Basis points, signed. Null when the previous period was zero. */
  changeBp: z.number().int().nullable(),
});

export const InsightOverviewSchema = z.object({
  from: Period,
  to: Period,
  comparedFrom: Period,
  comparedTo: Period,
  currency: z.enum(CURRENCY_CODES),
  storeCount: z.number().int(),
  figures: z.array(InsightFigureSchema),
  /** Every store, ranked by takings, so the tail is visible next to the head. */
  byStore: z.array(
    z.object({
      storeId: z.uuid(),
      storeName: z.string(),
      net: Money,
      previous: Money,
      orders: z.number().int(),
      covers: z.number().int(),
      perCover: Money,
    }),
  ),
});

export const INSIGHT_METRICS = ["net", "orders", "covers", "discount", "perCover"] as const;
export type InsightMetric = (typeof INSIGHT_METRICS)[number];

export const InsightTrendQuerySchema = InsightPeriodQuerySchema.extend({
  metric: z.enum(INSIGHT_METRICS).default("net"),
});

export const InsightTrendSchema = z.object({
  metric: z.enum(INSIGHT_METRICS),
  /** Money metrics carry a currency; counts do not. */
  currency: z.enum(CURRENCY_CODES).nullable(),
  dates: z.array(Period),
  series: z.array(
    z.object({
      storeId: z.uuid(),
      storeName: z.string(),
      /** One value per date, in the same order, zero-filled. */
      values: z.array(z.string()),
    }),
  ),
});

/**
 * How an item's price varies across the chain.
 *
 * The useful question is whether one location has drifted, so the answer is
 * shaped as the spread and the stores at its edges — not as a price list,
 * which the menu screen already shows.
 */
export const InsightPriceDistributionSchema = z.object({
  currency: z.enum(CURRENCY_CODES),
  /** How many items had drifted before `size` cut the list, so a client can say when it has. */
  total: z.number().int(),
  items: z.array(
    z.object({
      name: z.string(),
      storeCount: z.number().int(),
      min: Money,
      max: Money,
      median: Money,
      /** `max − min`, so a client need not subtract strings to sort by spread. */
      spread: Money,
      /** Where the extremes are, which is the whole point of the question. */
      lowest: z.array(z.string()),
      highest: z.array(z.string()),
    }),
  ),
});

/**
 * A flagged occurrence, with the evidence that produced it.
 *
 * `12-chain.md` § 5: "make each anomaly link to the evidence — the orders or
 * the report rows that triggered it."
 */
export const InsightAnomalySchema = z.object({
  ruleCode: z.enum(INSIGHT_RULE_CODES),
  labelKey: z.string(),
  storeId: z.uuid(),
  storeName: z.string(),
  businessDate: Period,
  /** The measured rate and the line it crossed, both in basis points. */
  valueBp: z.number().int(),
  thresholdBp: z.number().int(),
  direction: z.enum(["above", "below"]),
  /** The money behind the rate, so a 40% discount rate on $3 reads as small. */
  amount: Money,
  base: Money,
  currency: z.enum(CURRENCY_CODES),
  evidence: z.object({
    reportKey: z.string(),
    storeId: z.uuid(),
    from: Period,
    to: Period,
  }),
});

export const InsightRuleSettingSchema = z.object({
  ruleCode: z.enum(INSIGHT_RULE_CODES),
  labelKey: z.string(),
  reportKey: z.string(),
  thresholdBp: z.number().int(),
  defaultThresholdBp: z.number().int(),
  direction: z.enum(["above", "below"]),
  isEnabled: z.boolean(),
  /** Null for the chain default, a store id for an override. */
  storeId: z.uuid().nullable(),
});

export const UpdateInsightRuleSchema = z.object({
  thresholdBp: z.number().int().min(0).max(100_000),
  isEnabled: z.boolean().default(true),
  /** Absent sets the chain default; a store id sets that store's override. */
  storeId: z.uuid().nullable().default(null),
});

export type InsightOverview = z.infer<typeof InsightOverviewSchema>;
export type InsightAnomaly = z.infer<typeof InsightAnomalySchema>;
